import { pendingEvent, recordMethods } from "../domain/state.js";
import { resolveCapacity } from "../economy/entries.js";
import { ownerOf } from "../model/handles.js";
import { NumericFault } from "../numbers/types.js";
import { progressionContext } from "../progression/context.js";
import { applyReset, ResetTargetError } from "./reset.js";
export function makeTransaction(owner, working, purchaseCounts, allocations, productionTotals, scopeGenerations, definition, progression, random, tasks, calendars, markets, records, pendingEvents, gameTimeMs, numbers) {
    const clock = { value: gameTimeMs };
    const transaction = {};
    Object.assign(transaction, {
        numbers,
        gameTimeMs: () => clock.value,
        random: (path) => random.open(path),
        isScopeActive: (scope) => {
            if (ownerOf(scope) !== owner)
                throw new InvalidTarget(scope.id);
            return (definition.scopeActivations ?? [])
                .filter((activation) => activation.scope.id === scope.id)
                .every((activation) => activation.active(progressionContext(transaction)));
        },
        ...resourceMethods(owner, working, numbers),
        getPurchase: (id) => purchaseCounts[id] ?? numbers.fromNumber(0),
        setPurchase: (id, value) => {
            if (!numbers.isFinite(value))
                throw new NumericFault(`Invalid purchase count for ${id}`);
            purchaseCounts[id] = value;
        },
        getAllocation: (id, targetId) => allocations[id]?.[targetId] ?? numbers.fromNumber(0),
        setAllocation: (id, targetId, value) => {
            if (!numbers.isFinite(value))
                throw new NumericFault(`Invalid allocation for ${id}:${targetId}`);
            const allocation = allocations[id];
            if (!allocation || !(targetId in allocation))
                throw new InvalidTarget(`${id}:${targetId}`);
            allocation[targetId] = value;
        },
        addProduction: (id, executions) => {
            if (!numbers.isFinite(executions))
                throw new NumericFault(`Invalid production total for ${id}`);
            productionTotals[id] = numbers.add(productionTotals[id] ?? numbers.fromNumber(0), executions);
        },
        reset: (manifest) => applyReset(manifest, owner, working, purchaseCounts, allocations, scopeGenerations, definition, progression, tasks, calendars, markets, clock.value),
        ...progressionMethods(progression, numbers, () => clock.value),
        ...timedMethods(owner, tasks, calendars, markets),
        ...recordMethods(owner, records),
        emit: (eventType, payload, audience = { kind: "public" }) => {
            if (ownerOf(eventType) !== owner)
                throw new InvalidTarget(eventType.id);
            if (!(definition.domainEvents ?? []).includes(eventType))
                throw new InvalidTarget(eventType.id);
            pendingEvents.push(pendingEvent(eventType, clock.value, payload, audience));
        },
        reject: (error) => {
            throw new OperationRejected(error);
        },
    });
    transactionClocks.set(transaction, clock);
    return transaction;
}
function resourceMethods(owner, working, numbers) {
    const assertResource = (resource) => {
        if (ownerOf(resource) !== owner || !(resource.id in working))
            throw new InvalidTarget(resource.id);
    };
    const get = (resource) => {
        assertResource(resource);
        return working[resource.id];
    };
    const set = (resource, initialValue) => {
        assertResource(resource);
        if (!numbers.isFinite(initialValue))
            throw new NumericFault(`Invalid value for ${resource.id}`);
        const capacity = resolveCapacity(resource, get, numbers);
        let value = initialValue;
        if (capacity !== undefined && numbers.cmp(value, capacity) > 0) {
            if (resource.overflow === "block")
                throw new OperationRejected({
                    code: "capacity-blocked",
                    resourceId: resource.id,
                    attempted: value,
                    capacity,
                });
            value = capacity;
        }
        working[resource.id] = value;
    };
    return { get, set, add: (resource, amount) => set(resource, numbers.add(get(resource), amount)) };
}
function timedMethods(owner, tasks, calendars, markets) {
    return {
        getTaskState: (id) => requiredState(tasks, id, "task"),
        setTaskState: (id, state) => {
            requiredState(tasks, id, "task");
            tasks[id] = state;
        },
        getCalendarState: (calendar) => {
            if (ownerOf(calendar) !== owner)
                throw new InvalidTarget(calendar.id);
            return requiredState(calendars, calendar.id, "calendar");
        },
        setCalendarState: (calendar, state) => {
            if (ownerOf(calendar) !== owner)
                throw new InvalidTarget(calendar.id);
            requiredState(calendars, calendar.id, "calendar");
            calendars[calendar.id] = state;
        },
        getMarketState: (id) => requiredState(markets, id, "market"),
        setMarketState: (id, state) => {
            requiredState(markets, id, "market");
            markets[id] = state;
        },
    };
}
function requiredState(states, id, kind) {
    const state = states[id];
    if (!state)
        throw new InvalidTarget(`${kind}:${id}`);
    return state;
}
const transactionClocks = new WeakMap();
export function setTransactionTime(transaction, gameTimeMs) {
    const clock = transactionClocks.get(transaction);
    if (!clock)
        throw new TypeError("Transaction clock is unavailable");
    clock.value = gameTimeMs;
}
function progressionMethods(progression, numbers, gameTimeMs) {
    return {
        hasProgress: (kind, id) => Object.hasOwn(progressFlags(progression, kind), id),
        setProgress: (kind, id) => {
            if (Object.hasOwn(progressFlags(progression, kind), id))
                return;
            progressFlags(progression, kind)[id] = true;
            recordProgressionEvent(progression, kind, id, gameTimeMs());
        },
        hasActiveChallenges: () => progression.activeChallenges.size > 0,
        isChallengeActive: (id) => progression.activeChallenges.has(id),
        setChallengeActive: (id, active) => {
            if (active)
                progression.activeChallenges.add(id);
            else
                progression.activeChallenges.delete(id);
        },
        getChallengeCompletions: (id) => progression.challengeCompletions[id] ?? numbers.fromNumber(0),
        setChallengeCompletions: (id, value) => {
            if (!numbers.isFinite(value))
                throw new NumericFault(`Invalid challenge count for ${id}`);
            progression.challengeCompletions[id] = value;
        },
        hasReward: (id) => progression.rewardLedger.has(id),
        addReward: (id) => {
            if (progression.rewardLedger.has(id))
                return;
            progression.rewardLedger.add(id);
            recordProgressionEvent(progression, "challenge-reward", id, gameTimeMs());
        },
        getAutomation: (id) => progression.automation[id],
        setAutomation: (id, state) => {
            if (!Number.isSafeInteger(state.nextRunMs) || state.nextRunMs < 0)
                throw new TypeError(`Invalid automation schedule for ${id}`);
            progression.automation[id] = Object.freeze({ ...state });
        },
        setWon: (value) => {
            if (value && !progression.won)
                recordProgressionEvent(progression, "win", "game", gameTimeMs());
            progression.won = value;
        },
    };
}
function recordProgressionEvent(progression, kind, id, atGameMs) {
    const sequence = (progression.events.at(-1)?.sequence ?? 0n) + 1n;
    progression.events.push(Object.freeze({ sequence, kind, id, atGameMs }));
}
function progressFlags(progression, kind) {
    if (kind === "upgrade")
        return progression.upgrades;
    if (kind === "milestone")
        return progression.milestones;
    return progression.achievements;
}
class InvalidTarget extends Error {
    id;
    constructor(id) {
        super(`Invalid target: ${id}`);
        this.id = id;
    }
}
class OperationRejected extends Error {
    failure;
    constructor(failure) {
        super(failure.code);
        this.failure = failure;
    }
}
export function failureFrom(error) {
    if (error instanceof OperationRejected)
        return error.failure;
    if (error instanceof InvalidTarget)
        return { code: "invalid-target", id: error.id };
    if (error instanceof ResetTargetError)
        return { code: "invalid-target", id: error.id };
    if (error instanceof NumericFault)
        return { code: "numeric-fault", message: error.message };
    return {
        code: "transaction-failed",
        message: error instanceof Error ? error.message : String(error),
    };
}
//# sourceMappingURL=transaction.js.map